/**
 * 🔴 2026-10-14（第八轮）：分镜文案的**结构化呈现**。
 *
 * ## 为什么需要这个
 *
 * 用户原话：「看着信息太乱了帮我整理一下数据呈现的呈现格式，要便于阅读」。
 *
 * 真库取样（`data/aicomic.db`，镜 11）暴露的问题：
 * · `motion_script` **0 换行**，却是「0-2s: … 2-4s: … 4-6s: … 6-8s: …」四段时间轴
 *   —— 渲染成一坨段落，人眼要自己在脑内切段。
 * · `video_prompt` 只有 4 个换行，却塞了 `Duration:` / `时间轴：` / 正文 / 音轨禁令 / 文字禁令，
 *   还夹 6 处 `@图片N（名称）` 引用 —— 引用与正文混排，编号完全对不上号。
 *
 * ## 设计原则：**只改呈现，不改数据、不改文案**
 *
 * 🔴 铁律：解析器**不得重写、翻译、删改任何字**。只做「切段 + 识别已有标记」。
 * 切不出结构的（纯自然段）原样输出 —— 宁可不给样式，也不给错样式
 * （「装饰性错误信息比没有信息更糟」同族）。
 *
 * 识别的是**生成侧本来就写好的标记**，不是我发明的格式：
 * · `0-2s:` / `2-4s:` / `2.5-5.0s：` —— 时间码段（动作脚本/视频提示词通用）
 * · `起始状态：` / `结束状态：` —— 动作脚本首尾态
 * · `@图片N` / `@图片N（名称）` —— 参考图引用
 * · `Duration:` / `时间轴：` —— 视频提示词头部元信息
 */

/** 一段结构化文案 */
export type TextBlock =
  | { kind: "meta"; label: string; text: string }      // Duration: 10s
  | { kind: "timeline"; label: string; text: string }   // 0-2s: / 时间轴行
  | { kind: "state"; label: string; text: string }      // 起始状态 / 结束状态
  | { kind: "reference"; label: string; text: string }  // @图片N（名称）
  | { kind: "prose"; text: string };                    // 自然段

/** 时间码：`0-2s:` / `2.5-5.0s：` / `4-6s：`，允许冒号前后空格 */
const TIME_RE = /^(\d+(?:\.\d+)?)\s*[-–~]\s*(\d+(?:\.\d+)?)\s*s\s*[:：]\s*([\s\S]*)$/i;
/**
 * 🔴 切点用**无锚**的扫描正则：带 `^` 的正则做全局匹配时，`^` 只匹配整串开头，
 *    第二个时间码（`…没有声音。1.6-4.2s:`）就永远扫不到 ⇒ 四段时间轴被压成一段。
 *    这是本模块第一版的真 bug（真库镜 38 实测）。
 */
const TIME_SCAN_RE = /(\d+(?:\.\d+)?)\s*[-–~]\s*(\d+(?:\.\d+)?)\s*s\s*[:：]\s*/gi;
/** 状态标记：`起始状态：` / `结束状态：` */
const STATE_RE = /^(起始状态|结束状态|初始状态|末态)\s*[:：]\s*([\s\S]*)$/;
/** 参考图引用：`@图片2（观察位控制台）` / `@图片3: scene「404室…` */
const REF_RE = /^@图片\s*(\d+)\s*(?:[（(]([^）)]*)[）)])?\s*[:：]?\s*([\s\S]*)$/;
/** 头部元信息：`Duration: 10s.` / `时间轴：0.0-2.5s …` */
const META_RE = /^(Duration|时长|时间轴)\s*[:：]\s*([\s\S]*)$/i;

/**
 * 把一段长文案切成结构化块。
 *
 * 🔴 切不出任何结构时返回**单个 prose 块**（原样），
 * 这样调用方「有结构才上样式」的判断天然成立。
 */
export function parseShotText(raw: string | null | undefined): TextBlock[] {
  const text = (raw ?? "").trim();
  if (!text) return [];

  // 先按已有换行粗切，再对每段做标记识别
  const paras = text.split(/\n{1,}/).map((p) => p.trim()).filter(Boolean);
  const blocks: TextBlock[] = [];

  for (const para of paras) {
    // 🔴 先剥掉「起始状态：」/「结束状态：」两处标记再切时间码。
    //    不剥的话：开头的状态前缀会把**第一个**时间码一起吞进 state 块
    //    （真库镜 21/38 实测：4 段只剩 3 段），结尾的状态标记也会粘在末段 timeline 里。
    for (const seg of splitByStateCue(para)) {
      // 单段内可能还含多个时间码段（0 换行的 motion_script 就是这种）
      for (const t of splitByTimeCue(seg)) blocks.push(classify(t));
    }
  }
  return blocks;
}

/** 把 `起始状态：…` / `结束状态：…` 从所在段里摘出来，单独成块 */
function splitByStateCue(para: string): string[] {
  const out: string[] = [];
  let rest = para;

  // 开头：起始状态 / 初始状态
  const head = rest.match(/^(起始状态|初始状态|开始状态)\s*[:：]\s*([\s\S]*?)(?=(?:\d+(?:\.\d+)?\s*[-–~]\s*\d+(?:\.\d+)?\s*s\s*[:：])|$)/);
  if (head) {
    out.push(`${head[1]}：${head[2].trim()}`);
    rest = rest.slice(head[0].length).trim();
  }

  // 结尾：结束状态 / 末态（后面不再跟时间码）
  const tail = rest.match(/(?:^|(?<=[。；;]))\s*(结束状态|末态|结束)\s*[:：]\s*([\s\S]*)$/);
  if (tail) {
    const before = rest.slice(0, tail.index).trim();
    if (before) out.push(before);
    out.push(`${tail[1]}：${tail[2].trim()}`);
  } else if (rest) {
    out.push(rest);
  }
  return out.filter(Boolean);
}

/**
 * 一段里可能有多个 `0-2s:` / `2-4s:` 连续标记，逐个切开（保留前缀不丢）。
 *
 * 🔴 切点判定：标记前必须是**段首**或紧跟句读/换行 —— 否则句中的 "3-4s" 会被误切。
 */
function splitByTimeCue(para: string): string[] {
  const hits: Array<{ idx: number }> = [];
  const re = new RegExp(TIME_SCAN_RE.source, "gi");
  let m: RegExpExecArray | null;
  while ((m = re.exec(para)) !== null) {
    if (m.index === 0 || /[。；;！!？?\n]\s*$/.test(para.slice(0, m.index))) {
      hits.push({ idx: m.index });
    }
  }
  if (hits.length <= 1) return [para];

  const out: string[] = [];
  for (let i = 0; i < hits.length; i++) {
    // 首段从 0 起（可能含「起始状态：…」前缀），其余从本切点起
    const from = i === 0 ? 0 : hits[i].idx;
    const to = i + 1 < hits.length ? hits[i + 1].idx : para.length;
    out.push(para.slice(from, to).trim());
  }
  return out.filter(Boolean);
}

/** 给一段文本打标签（不改动内容） */
function classify(seg: string): TextBlock {
  const t = seg.trim();

  const time = t.match(TIME_RE);
  if (time) {
    return { kind: "timeline", label: `${time[1]}-${time[2]}s`, text: time[3].trim() };
  }
  const state = t.match(STATE_RE);
  if (state) {
    return { kind: "state", label: state[1], text: state[2].trim() };
  }
  const ref = t.match(REF_RE);
  if (ref) {
    // `@图片2（观察位控制台）` ⇒ label「图片2」，名称进 label 便于一眼认出是谁
    const name = ref[2] ? ref[2].trim() : "";
    return {
      kind: "reference",
      label: name ? `图片${ref[1]} · ${name}` : `图片${ref[1]}`,
      text: ref[3].trim(),
    };
  }
  const meta = t.match(META_RE);
  if (meta) {
    return { kind: "meta", label: meta[1], text: meta[2].trim() };
  }
  return { kind: "prose", text: t };
}

/** 该段是否「值得」用结构化样式（有 label 的才值得） */
export function isStructured(blocks: TextBlock[]): boolean {
  return blocks.some((b) => b.kind !== "prose");
}

/** 一段正文里的**内联**参考图引用（段首那类已单独成块，这里只管段中的） */
export interface InlineRef {
  text: string;
  label: string;
  name: string;
}

/**
 * 🔴 拆出正文里**内联**的 `@图片N（名称）`，供渲染层高亮。
 *
 * 真库实测（镜 1 video_prompt）：`在 @图片2（破败浴室镜前）昏暗潮湿的光线中，@图片1（林汐）双手…`
 * —— 引用是**夹在句子中间**的，一个段落里能出现十几次。
 * 这种不能切块（会把一句话劈成碎片），只能在原句里高亮，
 * 否则用户根本数不清「这条到底引用了几张参考图、分别是谁」。
 */
export function splitInlineRefs(text: string): Array<string | InlineRef> {
  const out: Array<string | InlineRef> = [];
  const re = /@图片\s*(\d+)\s*(?:[（(]([^）)]*)[）)])?/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const name = (m[2] ?? "").trim();
    out.push({ text: m[0], label: `图片${m[1]}`, name });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length > 0 ? out : [text];
}

/** 统计各类型块数量（真机脚本/门禁用来断言解析确实生效） */
export function countByKind(blocks: TextBlock[]): Record<TextBlock["kind"], number> {
  const out = { meta: 0, timeline: 0, state: 0, reference: 0, prose: 0 } as Record<
    TextBlock["kind"],
    number
  >;
  for (const b of blocks) out[b.kind]++;
  return out;
}